import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import NavLink from '@/components/Nav/NavLink';
import type { Link } from '@/components/Nav/NavLink';

/**
 * Small, muted section label that heads a nav group (Plays / Account / Staff …).
 * Purely visual — groups don't gate anything, they just make the sidebar
 * scannable instead of a flat wall of 12 equal-weight links.
 */
export function NavGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="px-3 font-semibold text-[0.6875rem] text-zinc-400 uppercase tracking-wider">
      {children}
    </h3>
  );
}

const isActive = (link: Link, path: string | undefined) =>
  path === link.href || (!!link.startsWithHref && !!path?.startsWith?.(link.startsWithHref));

/**
 * A collapsible nav group (used for "Guides"). Default COLLAPSED so the lower-
 * traffic reference links (Welcome Guide / Alert Guide / About RAS) don't compete
 * with the primary Plays and Account links.
 *
 * Collapse state persists sensibly:
 *   - Deterministic initial state on both server + client (avoids a hydration
 *     mismatch): expanded only when the current route is one of the group's own
 *     links, so a member who lands on /about isn't looking at a hidden active item.
 *   - After hydration we read the member's last choice from localStorage and honor
 *     it, so an explicit expand sticks across navigations.
 */
export function CollapsibleNavGroup({
  label,
  links,
  urlParsed,
  storageKey,
}: {
  label: string;
  links: Link[];
  urlParsed: { pathnameOriginal: string | undefined };
  storageKey: string;
}) {
  const onGroupPage = links.some((link) => isActive(link, urlParsed.pathnameOriginal));
  const [open, setOpen] = useState(onGroupPage);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === 'open') {
        setOpen(true);
      }
    } catch {
      // localStorage unavailable (private mode / SSR replays) — stay with the
      // route-derived default.
    }
  }, [storageKey]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, next ? 'open' : 'closed');
      } catch {
        // ignore persistence failures
      }
      return next;
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-sm px-3 py-1 font-semibold text-[0.6875rem] text-zinc-400 uppercase tracking-wider transition-colors hover:text-zinc-300"
      >
        <span>{label}</span>
        <ChevronRightIcon
          className={`size-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul role="list" className="-mx-2 mt-1 space-y-1 text-sm">
          {links.map((link) => (
            <NavLink key={link.href + link.text} link={link} urlParsed={urlParsed} />
          ))}
        </ul>
      )}
    </div>
  );
}
